import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Box, Button, Grid, IconButton } from "@mui/material";
import { Dispatch, MutableRefObject, SetStateAction } from "react";
import SignaturePad from "signature_pad";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import ErrorHelper from "../../../../../components/ErrorWrapper";
import Text from "../../../../../components/Text";
import SignatureInput from "../../../../../components/signiture/SignatureInput";

const arbitrationSections = [
  {
    title: "Article 1: Agreement to Arbitrate:",
    content:
      "It is understood that any dispute as to medical malpractice, that is as to whether any medical services rendered under this contract were unnecessary or unauthorized or were improperly, negligently, or incompetently rendered, will be determined by submission to arbitration as provided by California law, and not by a lawsuit or resort to court process except as California law provides for judicial review or arbitration proceedings. Both parties to this contract, by entering into it, are giving up their constitutional rights to have any such dispute decided in a court of law before a jury, and instead are accepting the use of arbitration.",
  },
  {
    title: "Article 2: All Claims Must be Arbitrated:",
    content:
      "It is the intention of the parties that this agreement bind all parties whose claims may arise out of or related to treatment or service provided by the physician including any spouse or heirs of the patient and any children, whether born or unborn, at the time of the occurrence giving rise to any claim. In the case of any pregnant mother, the term 'patient' herein shall mean the mother and the mother's expected child or children.\n\nAll claims for monetary damages exceeding the jurisdictional limit of the small claims court against the physician, and the physician's partners, associates, association, corporation or partnership, and the employees, agents and estates of any of them, must be arbitrated including, without limitation, claims for loss of consortium, wrongful death, emotional distress or punitive damages. Filing of any court by the physician to collect any fee from the patient shall not waive the right to compel arbitration of any malpractice claim.",
  },
  {
    title: "Article 3: Procedures and Applicable Law:",
    content:
      "A demand for arbitration must communicate in writing to all parties. Each party shall select an arbitrator (party arbitrator) within thirty days and a third arbitrator (neutral arbitrator) shall be selected by the arbitrators appointed by the parties within thirty days of a demand for a neutral arbitrator by either party. Each party to the arbitration shall pay such party's pro rata share of the expenses and fees of the neutral arbitrator, together with other expenses of the arbitration incurred or approved by the neutral arbitrator, not including counsel fees or witness fees, or other expenses incurred by a party for such party's own benefit. The parties agree that the arbitrators have the immunity of a judicial officer from civil liability when acting in the capacity of arbitrator under this contract. This immunity shall supplement, not supplant, any other applicable statutory or common law.\n\nEither party shall have the absolute right to arbitrate separately the issues of liability and damages upon written request to the neutral arbitrator.\n\nThe parties consent to the intervention and joinder in this arbitration of any person or entity which would otherwise be a proper additional party in a court action, and upon such intervention and joinder any existing court action against such additional person or entity shall be stayed pending arbitration.\n\nThe parties agree that the provisions of California law applicable to health care providers shall apply to disputes within this arbitration agreement, including, but not limited to, Code of Civil Procedure Section 340.5 and 667.7 and Civil Code Sections 3333.1 and 3333.2. Any party may bring before the arbitrations a motion for summary judgment or summary adjudication in accordance with the Code of Civil Procedure. Discovery shall be conducted pursuant to Code of Civil Procedure section 1283.05, however, depositions may be taken without prior approval of the neutral arbitrator.",
  },
  {
    title: "Article 4: General Provisions:",
    content:
      "All claims based upon the same incident, transaction or related circumstances shall be arbitrated in one proceeding. A claim shall be waived and forever barred if (1) on the date notice thereof is received, the claim, if asserted in a civil action, would be barred by the applicable California statute of limitations, or (2) the claimant fails to pursue the arbitration claim in accordance with the procedures prescribed herein with reasonable diligence. With respect to any matter not herein expressly provided for, the arbitrators shall be governed by the California Code of Civil Procedure provisions relating to arbitration.",
  },
  {
    title: "Article 5: Revocation:",
    content:
      "This agreement may be revoked by written notice delivered to the physician within 30 days, or signature. It is the intent of this agreement to apply to all medical services rendered any time for any condition.",
  },
  {
    title: "Article 6: Retroactive Effect:",
    content:
      "If patient intends this agreement to cover services rendered before the date it is Effective as of the date of first medical services.",
  },
];

const PhysicianPatientArbitrationAgreementForm = ({
  viewOnly,
  setFieldValue,
  values,
  canvasRef,
  signPad,
  isSignEmpty,
  setK,
}: // errors,
{
  viewOnly?: boolean;
  setFieldValue: (field: string, value: any) => void;
  values: any;
  canvasRef: MutableRefObject<HTMLCanvasElement | null>;
  signPad: SignaturePad | undefined;
  isSignEmpty: () => boolean;
  setK: Dispatch<SetStateAction<boolean>>;
  // errors: any;
}) => {
  return (
    <Box maxWidth={800} mx="auto" bgcolor="#fff">
      <Text variant="h5" fontWeight="bold" align="center" gutterBottom>
        Physician Patient Arbitration Agreement
      </Text>
      {arbitrationSections.map((section, idx) => (
        <Box key={idx} mb={2}>
          <Text variant="subtitle1" fontWeight="bold" gutterBottom>
            {section.title}
          </Text>
          <Text variant="body2" style={{ whiteSpace: "pre-line" }}>
            {section.content}
          </Text>
        </Box>
      ))}
      <Box mb={2}>
        <Text variant="body2">
          If any provision of this arbitration agreement is held invalid or
          unenforceable, the remaining provisions shall remain in full force and
          shall not be affected by the invalidity of any other provision.
        </Text>
        <Text variant="body2" mt={1}>
          I understand that I have the right to receive a copy of this
          arbitration agreement. By my signature below, I acknowledge that I
          have received a copy.
        </Text>
      </Box>
      <Box mb={2}>
        <Text variant="body2" fontWeight="bold">
          NOTICE: BY SIGNING THIS CONTRACT YOU ARE AGREEING TO HAVE ANY ISSUE OF
          MEDICAL MALPRACTICE DECIDED BY NEUTRAL ARBITRATION AND YOU ARE GIVING
          UP YOUR RIGHT TO A JURY OR COURT TRIAL. SEE ARTICLE 1 OF THIS
          CONTRACT.
        </Text>
      </Box>
      <Grid container spacing={2} alignItems="center" mt={2}>
        <Grid item xs={12} sm={6} lg={12}>
          <CustomDatePicker
            disabled={viewOnly}
            label="Date"
            value={values?.physicianPatientArbitrationAgreementDate}
            onChange={(val) => {
              setFieldValue(`physicianPatientArbitrationAgreementDate`, val);
            }}
          />
        </Grid>
        <Grid item container mt={1}>
          {viewOnly || values?.physicianPatientArbitrationAgreementSign ? (
            <Box>
              <ErrorHelper label={"Signature"}>
                <Box
                  style={{
                    position: "relative",
                    borderWidth: 1,
                    borderRadius: 5,
                    borderStyle: "solid",
                    height: "100%",
                  }}
                >
                  <img
                    src={values.physicianPatientArbitrationAgreementSign}
                    style={{ height: "150px", width: "360px" }}
                  />

                  {!viewOnly && (
                    <IconButton
                      color="error"
                      onClick={() => {
                        setK((prev) => !prev);
                        signPad?.clear();
                        setFieldValue(
                          "physicianPatientArbitrationAgreementSign",
                          undefined
                        );
                      }}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -20,
                        height: "10px",
                      }}
                    >
                      <HighlightOffIcon color="error" fontSize="medium" />
                    </IconButton>
                  )}
                </Box>
              </ErrorHelper>
            </Box>
          ) : (
            // <ErrorHelper
            //   error={errors?.physicianPatientArbitrationAgreementSign}
            // >
            <Box
              width={"360px"}
              position={"relative"}
              onMouseLeave={() => {
                if (!isSignEmpty() && signPad?.toDataURL()) {
                  setFieldValue(
                    "physicianPatientArbitrationAgreementSign",
                    signPad?.toDataURL()
                  );
                }
              }}
            >
              <SignatureInput
                // required
                // error={submitCount > 0 && isSignEmpty()}
                label="Signature"
                canvasRef={canvasRef}
                w="368px"
                h="100px"
              />
              <Button
                variant="contained"
                sx={{
                  position: "absolute",
                  bottom: 10,
                  left: 300,
                  height: "24px",
                }}
                onClick={() => {
                  setK((prev) => !prev);
                  signPad?.clear();
                }}
              >
                clear
              </Button>
            </Box>
            // </ErrorHelper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PhysicianPatientArbitrationAgreementForm;
